"use client";

import { useCallback, useContext, useMemo, useEffect, useRef } from "react";
import { AxiosResponse } from "axios";
import { QREditorContext } from "@/contexts/QREditorContext";
import { useSSOApiManager } from "./useSSOApiManager";
import { useSSOSuccessHandler } from "./useSSOSuccessHandler";
import { useSSOErrorHandler } from "./useSSOErrorHandler";
import { AuthPayloadBuilder } from "@/services/Auth/authPayloadBuilder";
import { SSOAuthLogicOptions, SSOAuthLogicResult, SSO_PROVIDER, SsoProviderId } from "../types";
import { PostLoginResponse, PostLoginResponseData } from "@/interface/api/postLogin/types";
import { PostRegisterResponse, PostRegisterResponseData } from "@/interface/api/postRegister/types";

/**
 * Type guard to check if data is an AxiosResponse
 */
function isAxiosResponse<T>(data: unknown): data is AxiosResponse<T> {
  return (
    data !== null &&
    typeof data === "object" &&
    "status" in data &&
    "data" in data &&
    typeof (data as AxiosResponse<T>).status === "number"
  );
}

/**
 * Hook that centralizes all SSO authentication business logic.
 * Handles API calls, success/error responses, and provides clean interface for components.
 * When provider is null, returns no-op handlers to prevent accidental API calls.
 */
export const useSSOAuth = ({
  provider,
  onSaveCookie,
  onSsoCompleted,
  accessToken,
  errors,
  setSsoLoading,
}: SSOAuthLogicOptions): SSOAuthLogicResult => {
  const qrEditorContext = useContext(QREditorContext);

  // Guard: if no provider is set, use Google as internal default but mark as invalid
  const effectiveProvider: SsoProviderId = provider ?? SSO_PROVIDER.GOOGLE;
  const isProviderValid = provider !== null;

  //Initialize API and handlers
  const { loginApi, registerApi } = useSSOApiManager(effectiveProvider);

  // Refs to avoid dependency cycles in useEffect
  const loginApiRef = useRef(loginApi);
  const registerApiRef = useRef(registerApi);
  loginApiRef.current = loginApi;
  registerApiRef.current = registerApi;

  const { handleLoginSuccess, handleRegisterSuccess } = useSSOSuccessHandler({
    onSaveCookie,
    onSsoCompleted,
    provider: effectiveProvider,
    registerSsoCredential: accessToken,
  });

  const { handleLoginError, handleRegisterError } = useSSOErrorHandler({
    provider: effectiveProvider,
    errors,
    setSsoLoading,
  });

  // Memoized data transformation
  const loginData = useMemo(() => {
    if (isAxiosResponse<PostLoginResponseData>(loginApi.data)) {
      return loginApi.data.data;
    }
    return undefined;
  }, [loginApi.data]);

  const registerData = useMemo(() => {
    if (isAxiosResponse<PostRegisterResponseData>(registerApi.data)) {
      return registerApi.data.data;
    }
    return undefined;
  }, [registerApi.data]);

  // Auth handlers - guard against invalid provider state
  const handleLogin = useCallback(
    (token: string) => {
      // Guard: don't proceed if provider is not valid
      if (!isProviderValid) {
        return;
      }
      // Clear previous error state before new request
      loginApi.reset();
      const payload = AuthPayloadBuilder.buildLoginPayload(
        effectiveProvider,
        token,
        qrEditorContext
      );
      loginApi.request(payload);
    },
    [isProviderValid, effectiveProvider, qrEditorContext, loginApi]
  );

  const handleRegister = useCallback(
    (token: string) => {
      // Guard: don't proceed if provider is not valid
      if (!isProviderValid) {
        return;
      }
      // Clear previous error state before new request
      registerApi.reset();
      const payload = AuthPayloadBuilder.buildRegisterPayload(
        effectiveProvider,
        token,
        qrEditorContext
      );
      registerApi.request(payload);
    },
    [isProviderValid, effectiveProvider, qrEditorContext, registerApi]
  );

  // Action triggering (success/error processing)
  useEffect(() => {
    if (loginData && !loginApi.loading) {
      const response: PostLoginResponse = { status: 200, data: loginData };
      handleLoginSuccess(response);

      // Reset login API state after successful login to prevent re-execution
      loginApiRef.current.reset();
    }
  }, [loginData, loginApi.loading, handleLoginSuccess]);

  useEffect(() => {
    if (loginApi.error && !loginApi.loading) {
      handleLoginError(loginApi.error);
      loginApiRef.current.reset();
    }
  }, [loginApi.error, loginApi.loading, handleLoginError]);

  useEffect(() => {
    if (registerData && !registerApi.loading && accessToken) {
      const response: PostRegisterResponse = { status: 201, data: registerData };
      handleRegisterSuccess(response);

      // Reset register API state after successful registration to prevent re-execution
      registerApiRef.current.reset();
    }
  }, [registerData, registerApi.loading, accessToken, handleRegisterSuccess]);

  useEffect(() => {
    if (registerApi.error && !registerApi.loading) {
      handleRegisterError(registerApi.error);
      registerApiRef.current.reset();
    }
  }, [registerApi.error, registerApi.loading, handleRegisterError]);

  // Reset function to clear API errors and data
  const resetApiState = useCallback(() => {
    loginApiRef.current.reset();
    registerApiRef.current.reset();
  }, []);

  return {
    handleLogin,
    handleRegister,
    resetApiState,
    isLoading: loginApi?.loading || registerApi?.loading,
    loginData,
    registerData,
    loginError: loginApi?.error,
    registerError: registerApi?.error,
  };
};
